import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

class MobilePaymentService {
  constructor() {
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    this.initializeAppListeners();
  }

  // Initialize app listeners for deep link handling
  initializeAppListeners() {
    if (Capacitor.isNativePlatform()) {
      // Override global User-Agent for mobile app
      this.overrideUserAgent();
      
      // Listen for app URL open events (deep links)
      App.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data.url);
        this.handleDeepLink(data.url);
      });

      // Listen for app state changes
      App.addListener('appStateChange', (state) => {
        console.log('App state changed:', state);
        if (state.isActive && this.paymentInProgress) {
          // App became active, check if we need to handle payment callback
          this.checkPendingPaymentCallback();
        }
      });
    }
  }

  // Override user agent for mobile app
  overrideUserAgent() {
    if (Capacitor.isNativePlatform()) {
      // Set custom user agent for CCAvenue mobile detection
      try {
        const originalUserAgent = navigator.userAgent;
        const mobileUA = originalUserAgent.includes('wv') ? 
          originalUserAgent : 
          originalUserAgent + ' wv';
        
        Object.defineProperty(navigator, 'userAgent', {
          get: () => mobileUA,
          configurable: true
        });
      } catch (error) {
        // Silently handle user agent override failure
      }
    }
  }

  // Check if we're running in a mobile app
  isMobileApp() {
    return Capacitor.isNativePlatform();
  }

  // Get mobile app user agent string
  getMobileUserAgent() {
    const platform = Capacitor.getPlatform();
    return `MyHomeEntryApp/1.0 (${platform}; wv) Capacitor`;
  }

  // Set custom headers for mobile app identification
  getMobileHeaders() {
    return {
      'User-Agent': this.getMobileUserAgent(),
      'X-App-Platform': 'capacitor',
      'X-App-Version': '1.0'
    };
  }

  // Handle payment initiation for mobile app using CCAvenue WebView approach
  async initiateMobilePayment(paymentData) {
    try {
      this.paymentInProgress = true;
      this.currentPaymentData = paymentData;

      console.log('Initiating CCAvenue WebView payment with data:', paymentData);

      // Store payment data in local storage for callback handling
      localStorage.setItem('pendingPayment', JSON.stringify(paymentData));
      localStorage.setItem('paymentStartTime', Date.now().toString());
      
     // According to CCAvenue mobile integration guide:
      // Open CCAvenue payment page in WebView and monitor for redirect/cancel URL
      const ccavenueUrl = this.buildCCAvenueURL(paymentData);
      
      console.log('Opening CCAvenue WebView with URL:', ccavenueUrl);
      
      // Use Browser plugin to open CCAvenue in WebView
      await Browser.open({
        url: ccavenueUrl,
        windowName: '_self',
        toolbarColor: '#2196F3',
        presentationStyle: 'fullscreen',
        showTitle: false // Hide title bar for better UX
      });

      // Listen for URL changes to detect payment completion
      // The WebView will redirect to our callback URL when payment is done
      this.setupWebViewListener();

      return { success: true };
    } catch (error) {
      console.error('Mobile payment initiation error:', error);
      this.paymentInProgress = false;
      this.currentPaymentData = null;
      localStorage.removeItem('pendingPayment');
      localStorage.removeItem('paymentStartTime');
      throw error;
    }
  }

  // Build CCAvenue URL according to official mobile integration guide
  buildCCAvenueURL(paymentData) {
    // CCAvenue mobile integration guide specifies these exact parameter names:
    // enc_val (or encRequest) and access_code (or accessCode)
    const encVal = paymentData.enc_val || paymentData.encRequest;
    const accessCode = paymentData.access_code || paymentData.accessCode;
    
    if (!encVal || !accessCode) {
      throw new Error('Missing required CCAvenue parameters: enc_val and access_code');
    }
    
    console.log('Building CCAvenue URL with enc_val:', encVal?.substring(0, 50) + '...', 'access_code:', accessCode);
    
    // According to CCAvenue mobile guide, use this exact URL format:
    // https://test.ccavenue.com/transaction.do?command=initiateTransaction&encRequest=enc_val&access_code=access_code
    const params = new URLSearchParams({
      command: 'initiateTransaction',
      encRequest: encVal,
      access_code: accessCode,
      // Add mobile app indicator for better detection in callback
      mobile_app: '1'
    });

    // Use test CCAvenue endpoint for development
    //const baseUrl = 'https://test.ccavenue.com/transaction.do';
    const baseUrl = 'https://secure.ccavenue.com/transaction.do';
    const finalUrl = `${baseUrl}?${params.toString()}`;
    
    console.log('Final CCAvenue URL with mobile indicator:', finalUrl);
    return finalUrl;
  }

  // Setup WebView listener to monitor for payment completion
  setupWebViewListener() {
    if (Capacitor.isNativePlatform()) {
      // According to CCAvenue guide: Monitor for redirect/cancel URL in WebView
      // When payment completes, CCAvenue redirects to our callback URL
      console.log('WebView listener ready - will detect payment completion via URL redirect');
      
      // Listen for Browser finish event (when WebView is closed or redirected)
      Browser.addListener('browserFinished', () => {
        console.log('Browser finished - WebView closed');
        this.handleWebViewClosed();
      });
      
      // Listen for page load to detect custom scheme redirects
      Browser.addListener('browserPageLoaded', () => {
        console.log('Browser page loaded');
      });
    }
  }

  // Handle WebView closure
  handleWebViewClosed() {
    console.log('WebView closed, checking for pending payment...');
    
    // Check if we have a pending payment that might have been completed
    const pendingPayment = localStorage.getItem('pendingPayment');
    if (pendingPayment && this.paymentInProgress) {
      console.log('Payment was in progress when WebView closed - assuming success');
      
      // According to CCAvenue mobile guide: if WebView reaches callback URL and closes,
      // it means payment was completed (success or failure)
      // Since we can't get the exact status from WebView closure, we navigate to callback page
      // and let the PaymentCallback component handle the success logic
      
      // Don't clear payment state here - let PaymentCallback component handle it
      console.log('WebView closed during payment - navigating to callback page for processing');
      
      // Navigate to payment callback page to trigger success detection
      setTimeout(() => {
        window.location.href = '/payment-callback';
      }, 500); // Small delay to ensure WebView is fully closed
    }
  }

  // Handle deep link callback from CCAvenue
  handleDeepLink(url) {
    try {
      console.log('=== DEEP LINK RECEIVED ===');
      console.log('Deep link URL:', url);
      console.log('Current timestamp:', new Date().toISOString());
      
      const urlObj = new URL(url);
      console.log('URL Protocol:', urlObj.protocol);
      console.log('URL Pathname:', urlObj.pathname);
      console.log('URL Search:', urlObj.search);
      
      // Check if this is a payment callback
      // For URL: myhomeentry://payment-callback?... the pathname will be '/payment-callback'
      if (urlObj.protocol === 'myhomeentry:' && (urlObj.pathname === '//payment-callback' || urlObj.pathname === '/payment-callback' || url.includes('payment-callback'))) {
        console.log('=== PAYMENT CALLBACK DEEP LINK DETECTED ===');
        const params = new URLSearchParams(urlObj.search);
        console.log('Payment callback parameters:', params.toString());
        
        this.processPaymentCallback(params);
      } else {
        console.log('Deep link is not a payment callback, ignoring');
      }
    } catch (error) {
      console.error('Deep link handling error:', error);
    }
  }

  // Process payment callback from deep link
  async processPaymentCallback(params) {
    try {
      console.log('=== PROCESSING PAYMENT CALLBACK ===');
      console.log('Callback timestamp:', new Date().toISOString());
      console.log('Processing payment callback with params:', params.toString());
      
      const paymentResult = {
        status: params.get('status'),
        orderId: params.get('orderId'),
        transactionId: params.get('transactionId'),
        amount: params.get('amount'),
        message: params.get('message'),
        paymentId: params.get('paymentId'),
        flatId: params.get('flatId'),
        buildingId: params.get('buildingId'),
        error: params.get('error')
      };

      console.log('Extracted payment result:', JSON.stringify(paymentResult, null, 2));

      // Store payment result in localStorage for PaymentCallback component BEFORE clearing pending payment
      localStorage.setItem('mobilePaymentResult', JSON.stringify(paymentResult));
      
      // Clear payment in progress flags AFTER storing result
      this.paymentInProgress = false;
      this.currentPaymentData = null;
      localStorage.removeItem('pendingPayment');
      localStorage.removeItem('paymentStartTime');
      console.log('Payment in progress flags cleared after storing result');
      
      // According to CCAvenue mobile guide: Navigate to success page to show payment status
      // Use React Router navigation for proper SPA behavior
      console.log('Payment completed, triggering navigation to payment callback page...');
      
      try {
        // Build URL with all payment parameters like web version
        const urlParams = new URLSearchParams();
        urlParams.set('status', paymentResult.status || 'Unknown');
        urlParams.set('orderId', paymentResult.orderId || '');
        urlParams.set('paymentId', paymentResult.paymentId || '');
        urlParams.set('flatId', paymentResult.flatId || '');
        urlParams.set('buildingId', paymentResult.buildingId || '');
        urlParams.set('transactionId', paymentResult.transactionId || '');
        urlParams.set('amount', paymentResult.amount || '');
        urlParams.set('message', paymentResult.message || (paymentResult.status === 'Success' ? 'Payment completed successfully' : 'Payment failed'));
        urlParams.set('mobile', 'true');
        
        // Add error parameter for failed payments
        if (paymentResult.status !== 'Success' && paymentResult.error) {
          urlParams.set('error', paymentResult.error);
        }
        
        // Use full page navigation to ensure proper route handling
        const callbackUrl = `/payment-callback?${urlParams.toString()}`;
        console.log('Navigating to payment callback URL:', callbackUrl);
        
        // Force full page navigation to ensure proper React Router handling
        window.location.href = callbackUrl;
      } catch (navigationError) {
        console.error('Navigation error:', navigationError);
        // Fallback navigation without parameters
        window.location.href = '/payment-callback?error=' + encodeURIComponent(navigationError.message) + '&mobile=true';
      }

    } catch (error) {
      console.error('Payment callback processing error:', error);
      
      // Store error result and navigate to error page
      const errorResult = {
        status: 'Failed',
        error: error.message,
        message: 'Payment processing error'
      };
      localStorage.setItem('mobilePaymentResult', JSON.stringify(errorResult));
      
      // Navigate to payment callback with error
      window.location.href = `/payment-callback?error=${encodeURIComponent(error.message)}&mobile=true`;
    }
  }

  // Check for pending payment callback (when app becomes active)
  checkPendingPaymentCallback() {
    const pendingPayment = localStorage.getItem('pendingPayment');
    const paymentStartTime = localStorage.getItem('paymentStartTime');
    
    if (pendingPayment && paymentStartTime) {
      const startTime = parseInt(paymentStartTime);
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      
      // If more than 5 minutes have passed, assume payment was abandoned
      if (elapsedTime > 5 * 60 * 1000) {
        console.log('Payment timeout detected, cleaning up...');
        this.paymentInProgress = false;
        this.currentPaymentData = null;
        localStorage.removeItem('pendingPayment');
        localStorage.removeItem('paymentStartTime');
      } else {
        console.log('Pending payment detected, waiting for callback...');
      }
    }
  }

  // Check if a payment is currently in progress
  isPaymentInProgress() {
    return this.paymentInProgress;
  }

  // Get current payment data
  getCurrentPaymentData() {
    return this.currentPaymentData;
  }

  // Cancel current payment
  cancelPayment() {
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('paymentStartTime');
    
    // Close browser if open
    if (Capacitor.isNativePlatform()) {
      Browser.close();
    }
  }

  // Initialize mobile payment service
  static getInstance() {
    if (!this.instance) {
      this.instance = new MobilePaymentService();
    }
    return this.instance;
  }
}

export default MobilePaymentService;
export const mobilePaymentService = MobilePaymentService.getInstance();